import React, { useState, useEffect, useContext, forwardRef, useRef, useImperativeHandle } from "react";
import { Button } from "../../components/Shared/Buttons/Button";
import { BasicDetails } from "./BasicDetails";
import { VacancyQuestions } from "./VacancyQuestions";
import { ReplyMail } from "./ReplyMail";
import { submitVacancy } from "../../services/VacancyService";
import { LoggedRecruiter } from "../../models/LoggedRecruiter";
import { AuthContext } from "../../providers/AuthContextProvider";
import { useNavigate } from "react-router-dom";
import useErrorPage from "../../hooks/useErrorPage";
import { ReactComponent as TickMark } from "../../assets/svg/tick_circles.svg";
import { PopUpWindow } from '../Shared/PopUpWindow';
import { useMediaQuery } from 'react-responsive';
import { ResponseModel } from "../../models/ResponseModel";
import { useAlert } from "../../hooks/useAlert";
import { SummaryOfCompletionModel } from "../../models/SummaryOfCompletionModel";
import { CVLessQuestions } from "./CVLessQuestions";
import { TabComponentRef } from "../../models/TabComponentRef";

interface Props {  
    styles: any;
    jobId?: number;
    onUpdateSummary: (model?: Partial<SummaryOfCompletionModel>) => void;
    onTabChange: (tab: string) => void;
    onPreview: () => void;
    onJobIdUpdate: (jobId: number) => void;
    onCVLessModeChange?: (isCVLess: boolean) => void;
    isCVLess: boolean;
}

export const ReadyToSubmit = forwardRef<TabComponentRef, Props>((props, ref) => {
    const jobId = props.jobId;
    const styles = props.styles;
    const navigate = useNavigate();    
    const authContext = useContext(AuthContext);    
    const recruiter: LoggedRecruiter = authContext.getLoggedRecruiter();
    const [currentTab, setCurrentTab] = useState<string>("basicDetails");
    const [openMessageDialog, setOpenMessageDialog] = useState<boolean>(false);
    const { Alert, openAlert } = useAlert();
    const [toErrorPage] = useErrorPage();
    const isMobile = useMediaQuery({ query: "(max-width: 786px)" });
    const basicDetailsRef = useRef<TabComponentRef>(null);
    const vacancyRef = useRef<TabComponentRef>(null);
    const cvLessRef = useRef<TabComponentRef>(null);
    const replyMailRef = useRef<TabComponentRef>(null);
    const [submitting, setSubmitting] = useState(false);
    const [switchingTab, setSwitchingTab] = useState(false);
    
    const handleTabChange = (tab: string) => {
        if (tab === "readyToSubmit") return;
        setCurrentTab(tab);
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            window.scrollTo(0, 0);
        }, 0);

        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (props.isCVLess && currentTab === "vacancyQuestions") {
            setCurrentTab("cvLessQuestions");
        } else if (!props.isCVLess && currentTab === "cvLessQuestions") {
            setCurrentTab("vacancyQuestions");
        }
    }, [props.isCVLess]);

    const getRefForTab = (tab: string): React.RefObject<TabComponentRef> | undefined => {
        switch (tab) {
            case "basicDetails": return basicDetailsRef;
            case "vacancyQuestions": return vacancyRef;
            case "cvLessQuestions": return cvLessRef;
            case "replyToMail": return replyMailRef;
            default: return undefined;
        }
    };

    const handleButtonClick = async (tab: string) => {
        if (tab === currentTab) return;

        const currentRef = getRefForTab(currentTab);
        setSwitchingTab(true);
        const success = await currentRef?.current?.save();
        setSwitchingTab(false);

        if (!success) {
            return; 
        }

        setCurrentTab(tab);
    };

    const handleBack = () => {
        props.onTabChange("replyToMail");
    };


    const handleViewJobs = () => {
        setOpenMessageDialog(false);
        navigate('/recruiter/vacancy?page=1&status=PENDING', { replace: true });
    }

    async function handleSubmitClick(event?: React.FormEvent<HTMLFormElement>): Promise<boolean> {
        event?.preventDefault();
        setSubmitting(true);
        const tabsToSave: { key: string; ref: React.RefObject<TabComponentRef> }[] = [
            { key: "basicDetails", ref: basicDetailsRef },
            props.isCVLess
                ? { key: "cvLessQuestions", ref: cvLessRef }
                : { key: "vacancyQuestions", ref: vacancyRef },
            { key: "replyToMail", ref: replyMailRef },
        ];

        for (const tab of tabsToSave) {
            const success = await tab.ref.current?.save();
            if (!success) {
                setCurrentTab(tab.key);
                setSubmitting(false);
                return false;
            }
        }

        try {
            const response: ResponseModel = await submitVacancy(recruiter.recruiterId, recruiter.token, jobId);
            if (response.status) {
                setOpenMessageDialog(true);
                return true;
            } else {
                openAlert("error", "There was a problem with your submission. Please try again later.");
                return false; 
            }
        } catch (error) {
            toErrorPage(error);
            return false; 
        } finally {
            setSubmitting(false);
        }
    }

    useImperativeHandle(ref, () => ({
        save: () => handleSubmitClick()
    }));
    
    return(
        <div style={styles.readyToSubmit.mainWrapper}>
            {!isMobile && <div style={styles.readyToSubmit.subTopic}>{"Once it’s approved, your advert will go live"}</div>}
            {isMobile &&  <Button style={styles.readyToSubmit.buttonsWrapper.viewButton} onClick={props.onPreview}>{"VIEW HOW ITS LOOKS"}</Button>}
            <div style={styles.readyToSubmit.tabsWrapper}>
                <div style={styles.readyToSubmit.tabs}>
                    <Button 
                        type="button" 
                        disabled={switchingTab}
                        style={{...styles.readyToSubmit.tab, ...(currentTab === "basicDetails" && styles.readyToSubmit.tabActive)}} 
                        onClick={() => handleButtonClick("basicDetails")}
                    >
                        {"Basic Info"}
                    </Button>
                    {props.isCVLess ? <Button 
                        type="button" 
                        disabled={switchingTab}
                        style={{...styles.readyToSubmit.tab, ...(currentTab === "cvLessQuestions" && styles.readyToSubmit.tabActive)}} 
                        onClick={() => handleButtonClick("cvLessQuestions")}
                    >
                        {"CV Less Questions"}
                    </Button>
                    : <Button 
                        type="button" 
                        disabled={switchingTab}
                        style={{...styles.readyToSubmit.tab, ...(currentTab === "vacancyQuestions" && styles.readyToSubmit.tabActive)}} 
                        onClick={() => handleButtonClick("vacancyQuestions")}
                    >
                        {"Vacancy Questions"}
                    </Button>}
                    <Button 
                        type="button" 
                        disabled={switchingTab}
                        style={{...styles.readyToSubmit.tab, ...(currentTab === "replyToMail" && styles.readyToSubmit.tabActive)}} 
                        onClick={() => handleButtonClick("replyToMail")}
                    >
                        {"Reply Mail"}
                    </Button>
                </div>
            </div>
            <div style={{ display: currentTab === "basicDetails" ? "block" : "none" }}>
                <BasicDetails
                    ref={basicDetailsRef}
                    styles={styles.basicDetails}
                    onUpdateSummary={props.onUpdateSummary}
                    onTabChange={handleTabChange}
                    onJobIdUpdate={props.onJobIdUpdate}
                    onCVLessModeChange={props.onCVLessModeChange}
                    jobId={jobId}
                    isFinalStage={true}
                />
            </div>
            <div style={{ display: currentTab === "vacancyQuestions" ? "block" : "none" }}>
                <VacancyQuestions
                    ref={vacancyRef}
                    styles={styles.vacancyQuestion}
                    onUpdateSummary={props.onUpdateSummary}
                    onTabChange={handleTabChange}
                    jobId={jobId}
                    isFinalStage={true}
                />
            </div>
            <div style={{ display: currentTab === "cvLessQuestions" ? "block" : "none" }}>
                <CVLessQuestions
                    ref={cvLessRef}
                    styles={styles.cvLessQuestion}
                    onUpdateSummary={props.onUpdateSummary}
                    onTabChange={handleTabChange}
                    jobId={jobId}
                    isFinalStage={true}
                />
            </div>
            <div style={{ display: currentTab === "replyToMail" ? "block" : "none" }}>
                <ReplyMail
                    ref={replyMailRef}
                    styles={styles.replyMail}
                    onUpdateSummary={props.onUpdateSummary}
                    onTabChange={handleTabChange}
                    jobId={jobId}
                    isCVLess={props.isCVLess}
                    isFinalStage={true}
                />
            </div>
            <div style={styles.readyToSubmit.buttonsWrapper}>
                <Button style={styles.readyToSubmit.buttonsWrapper.back} onClick={handleBack}>{"BACK"}</Button>
                <Button style={styles.readyToSubmit.buttonsWrapper.next} disabled={submitting} onClick={handleSubmitClick}>{"SUBMIT"}</Button>         
            </div> 
            <PopUpWindow styles={styles.readyToSubmit.jobSubmisionDialog} open={openMessageDialog} toggle={handleViewJobs} closeCircle={true}>
                <TickMark style={styles.readyToSubmit.tickMark}/>
                <span style={styles.readyToSubmit.deleteDialogTitle}>{"Job successfully added to the system"}</span>
                <p style={styles.readyToSubmit.popupText}>{"Your Job is being processed by the Xpressjobs team and will be posted on the site shortly."}</p>
                <div className="center-content">
                    <Button style={{...styles.readyToSubmit.jobViewButton, ...styles.readyToSubmit.jobViewButton.blue}} onClick={handleViewJobs}>{"VIEW ALL MY JOBS"}</Button> 
                </div>
            </PopUpWindow>
            <Alert/>
        </div>
    )
})
